import { Box } from '@mui/material'
import React, { useEffect, useMemo, useRef, useState } from 'react'

import { AgGridCellValueMethod } from '@/enums/axiom-report.enum'
import { useAppSelector } from '@/hooks/use-app-selector'
import { ITransactionReports } from '@/interfaces/portfolioOptimization.interface'
import {
  getTransactionReports,
  getUpdatedCellValue,
} from '@/services/portfolio-optimization/optimization-reports.service'
import { showSnackbar } from '@/states/snackbar.slice'
import { portfolioOptimizationDefaultGridOptions } from '@/utils/grid-utils/colDefs'
import {
  GridOptions,
  GridReadyEvent,
  ValueFormatterParams,
  ValueGetterParams,
} from '@ag-grid-community/core'
import { AgGridReact } from '@ag-grid-community/react'
import { useDispatch } from 'react-redux'
import { TransactionReportColumns } from './TransactionReportColumns'

const TransactionReport: React.FC<{
  setGridReference: Function
}> = ({ setGridReference }) => {
  const appDispatch = useDispatch()
  const selectedReport =
    useAppSelector((s) => s.axiomReport.selectedReport) || {}
  const [transactionReportsData, setTransactionReportsData] = useState<
    ITransactionReports[]
  >([])
  const [loading, setLoading] = useState(true)
  const gridRef = useRef<AgGridReact>(null)
  const gridOptions = {
    ...portfolioOptimizationDefaultGridOptions,
    noRowsOverlayComponentParams: () => {
      return {
        noRowsMessage: 'No data available.',
      }
    },
    rowSelection: 'multiple' as const,
    columnDefs: TransactionReportColumns.map((item) => {
      if (item.cellDataType === 'number') {
        ;(item.valueGetter = (params: ValueGetterParams) =>
          getUpdatedCellValue(
            params.data,
            params.colDef.field as string,
            AgGridCellValueMethod.VALUE_GETTER,
          )),
          (item.valueFormatter = (params: ValueFormatterParams) =>
            getUpdatedCellValue(
              params.data,
              params.colDef.field as string,
              AgGridCellValueMethod.VALUE_FORMATTER,
            ))
      }
      return item
    }),
  } as GridOptions

  const onGridReady = (params: GridReadyEvent) => {
    setGridReference(gridRef)
  }

  const gridStyle = useMemo(
    () => ({
      height: '100%',
      minHeight: '300px',
      flex: '1 1 auto',
    }),
    [],
  )

  useEffect(() => {
    setLoading(true)
    const fetchTransactionReports = async () => {
      try {
        const response = await getTransactionReports(selectedReport.reportId)
        setTransactionReportsData(response)
      } catch (err) {
        appDispatch(
          showSnackbar({
            message: `Error fetching transaction reports ${err instanceof Error ? err.message : String(err)}`,
            severity: 'error',
          }),
        )
        setTransactionReportsData([])
      } finally {
        setLoading(false)
      }
    }
    fetchTransactionReports()
  }, [selectedReport.reportId, appDispatch])
  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        padding: 2,
        gap: 1,
        height: '100%',
        minHeight: '300px',
        maxHeight: '100vh',
        overflow: 'hidden',
      }}
    >
      <Box
        className='ag-theme-quartz transaction-reports-table'
        style={gridStyle}
      >
        <AgGridReact
          ref={gridRef}
          onGridReady={onGridReady}
          rowData={transactionReportsData}
          gridOptions={gridOptions}
          data-cy='transaction-report-ag-grid'
          loading={loading}
        ></AgGridReact>
      </Box>
    </Box>
  )
}

export default TransactionReport
